import { Component, DoCheck, Input } from "@angular/core";
import { Organisation, Project } from "../../common/organisation";
import { USER_ROLE, getUserRoleName } from "../../common/user";
import { FUNCTION, FunctionService } from "../../model-services/function.service";
import { Router } from "@angular/router";
import { PROJECTS_URL } from "../../app-urls";
import { ModelService } from "../models/model-service";
import { Column } from "../../ui-components/table/table-component";

// what the table shows for a project; actions look the project up again by _key
interface ProjectRow {
    _key:string
    name:string
    type:string
    startDate:Date
    completionDate:Date
    status:string
    access:string
    accessControl:string
    canEdit:boolean
    canDelete:boolean
}

@Component({
    selector: 'app-project-table',
    templateUrl: './project-table.html',
    standalone: false
})
export class ProjectTableComponent implements DoCheck {
    @Input() organisation:Organisation

    columns:Column[] = []
    data:ProjectRow[] = []

    // projects are added, edited and removed through the organisation service, so watch for a new list
    private shownProjects:Project[]

    constructor(private functionService:FunctionService, private router:Router, private modelService:ModelService) {
    }

    ngDoCheck() {
        if (this.organisation && this.organisation.projects !== this.shownProjects) this.refresh()
    }

    private refresh() {
        this.shownProjects = this.organisation.projects
        const projects = this.shownProjects || []

        this.data = projects
            .map(p => ({
                _key: p._key,
                name: p.name,
                type: p.sandbox ? 'Sandbox' : 'Change',
                startDate: p.startDate,
                completionDate: p.completionDate,
                status: this.getStatus(p),
                access: getUserRoleName(p.role),
                accessControl: p.useOrganisationAccess ? 'Organisation' : 'Project users',
                canEdit: this.isAdmin(p),
                canDelete: this.isAdmin(p),
            }))
            // most recent first
            .sort((a, b) => new Date(b.startDate || 0).getTime() - new Date(a.startDate || 0).getTime())

        this.columns = [
            new Column('name', 'Name', 'string'),
            new Column('type', 'Type', 'string'),
            new Column('startDate', 'Started', 'date'),
            new Column('completionDate', 'Target completion', 'date'),
            new Column('status', 'Status', 'string'),
            new Column('access', 'Your access', 'string'),
            new Column('accessControl', 'Access from', 'string'),
        ]
        if (projects.some(p => this.isAdmin(p))) {
            this.columns.push(new Column('canEdit', 'Edit', 'icon-button', 'Edit project details and access', 'accent', 'edit'))
            this.columns.push(new Column('canDelete', 'Delete', 'icon-button', 'Delete this project and its models', 'warn', 'delete'))
        }
    }

    private getStatus(project:Project) {
        const now = Date.now()
        if (project.startDate && new Date(project.startDate).getTime() > now) return 'Not started'
        if (project.completionDate && new Date(project.completionDate).getTime() < now) return 'Past target'
        return 'In progress'
    }

    async handleRowEvent(rowEvent:{row:any, column?:Column}) {
        const project = rowEvent.row && this.organisation.projects.find(p => p._key === rowEvent.row._key)
        if (!project) return
        if (!rowEvent.column) this.openProject(project)
        else if (rowEvent.column.name === "canDelete") this.deleteProject(project)
        else if (rowEvent.column.name === "canEdit") this.updateProjectDetails(project)
    }

    private isAdmin(project:Project) {
        return project.role === USER_ROLE.ADMIN
    }

    updateProjectDetails(project:Project) {
        if (this.isAdmin(project))
            this.functionService.executeFunction(FUNCTION.EDIT_PROJECT, undefined, undefined, project)
    }

    // without access, clicking a project asks for it
    openProject(project:Project) {
        if (project.role) this.router.navigateByUrl(PROJECTS_URL(this.organisation._key, project._key))
        else this.modelService.requestAccessToProject(project)
    }

    deleteProject(project:Project) {
        if (this.isAdmin(project))
            this.functionService.executeFunction(FUNCTION.DELETE_PROJECT, undefined, undefined, project)
    }
}
